/**
 * Hero parallax — vertical scroll offset for hero logos and floating images.
 *
 * Why JS rather than CSS scroll-driven animation: the hero lives inside
 * `carousel-slides`, which sets `overflow-x: scroll`. That makes its
 * `overflow-y` compute to `auto`, so it becomes the nearest scroll container in
 * the block axis and `animation-timeline: view(block)` would resolve against it
 * (a container that never scrolls vertically) instead of the page. So progress
 * is measured against the viewport directly.
 *
 * Contract:
 * - Opt in with `data-hero-parallax` on the element to move.
 * - `data-hero-parallax-strength` — peak travel in px (default 40).
 * - `data-hero-parallax-direction` — 'up' or 'down' (default 'up').
 * - Writes `--hero-parallax-y`; the element's CSS is responsible for consuming
 *   it (see blocks/_hero-logo.liquid and blocks/_hero-floating-image.liquid,
 *   where a dedicated wrapper owns `translate` so it cannot collide with the
 *   rotation or bob animation).
 */

const SELECTOR = '[data-hero-parallax]';
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Elements currently intersecting the viewport — the only ones worth updating. */
const active = new Set();

let observer = null;
let frame = null;
let listening = false;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

/**
 * Progress of an element through the viewport, normalised to [-1, 1]:
 * -1 just entering from the bottom, 0 centred, +1 leaving past the top.
 */
function viewportProgress(rect, viewportHeight) {
  const elementCentre = rect.top + rect.height / 2;
  const span = viewportHeight / 2 + rect.height / 2;
  if (span <= 0) return 0;
  return clamp((viewportHeight / 2 - elementCentre) / span, -1, 1);
}

function apply(element, viewportHeight) {
  const rect = element.getBoundingClientRect();

  // A collapsed rect means the slide is hidden (display:none, or a
  // content-visibility:auto slide that has not been rendered yet).
  if (rect.height === 0 && rect.width === 0) return;

  const strength = Number.parseFloat(element.dataset.heroParallaxStrength) || 40;
  const sign = element.dataset.heroParallaxDirection === 'down' ? 1 : -1;
  const shift = viewportProgress(rect, viewportHeight) * strength * sign;

  element.style.setProperty('--hero-parallax-y', `${shift.toFixed(2)}px`);
}

function update() {
  frame = null;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  for (const element of active) apply(element, viewportHeight);
}

function requestUpdate() {
  if (frame !== null) return;
  frame = window.requestAnimationFrame(update);
}

function reset(element) {
  element.style.setProperty('--hero-parallax-y', '0px');
}

function stopListening() {
  if (!listening) return;
  window.removeEventListener('scroll', requestUpdate);
  window.removeEventListener('resize', requestUpdate);
  listening = false;
}

function startListening() {
  if (listening) return;
  window.addEventListener('scroll', requestUpdate, { passive: true });
  window.addEventListener('resize', requestUpdate, { passive: true });
  listening = true;
}

function getObserver() {
  if (observer) return observer;

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          active.add(entry.target);
        } else {
          active.delete(entry.target);
          // Park it at rest so a re-entry does not start from a stale offset.
          reset(entry.target);
        }
      }

      if (active.size > 0) {
        startListening();
        requestUpdate();
      } else {
        stopListening();
      }
    },
    // A margin keeps motion smooth right at the viewport edges.
    { rootMargin: '10% 0px 10% 0px' }
  );

  return observer;
}

function teardown() {
  stopListening();
  if (frame !== null) {
    window.cancelAnimationFrame(frame);
    frame = null;
  }
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  for (const element of active) reset(element);
  active.clear();
}

function scan(root = document) {
  if (REDUCED_MOTION.matches) {
    teardown();
    for (const element of root.querySelectorAll(SELECTOR)) reset(element);
    return;
  }

  const io = getObserver();
  for (const element of root.querySelectorAll(SELECTOR)) io.observe(element);
}

scan();

// Re-scan when the theme editor swaps a section in, and when the visitor
// changes their motion preference mid-session.
document.addEventListener('shopify:section:load', (event) => scan(event.target));
document.addEventListener('shopify:block:select', () => requestUpdate());
REDUCED_MOTION.addEventListener('change', () => scan());
